/**
 * FollowupsSection — R3
 * Shows existing follow-ups for an invoice on InvoiceDetailPage.
 * Actions: mark done, dismiss, snooze (1/3/7 days).
 * Optimistic updates via usePatchFollowup.
 */
import {
  useInvoiceFollowups,
  usePatchFollowup,
  useDeleteFollowup,
  type CollectionFollowup,
} from '@/hooks/useCollectionFollowups';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CheckCircle2, X, Clock, Bell } from 'lucide-react';
import { todayLocalDateString, addLocalDays } from '@/lib/localDate';

interface FollowupsSectionProps {
  invoiceId: string;
}

function formatDate(isoDate: string): string {
  return new Date(isoDate + 'T00:00:00').toLocaleDateString('en-CA', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(
    cents / 100
  );
}

function isOverdue(dueDateStr: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDateStr + 'T00:00:00');
  return due < today;
}

function addDays(dateStr: string, days: number): string {
  return addLocalDays(dateStr, days) ?? dateStr;
}

function FollowupRow({
  followup,
  invoiceId,
}: {
  followup: CollectionFollowup;
  invoiceId: string;
}) {
  const patchFollowup = usePatchFollowup(invoiceId);
  const deleteFollowup = useDeleteFollowup(invoiceId);
  const overdue = isOverdue(followup.due_date);
  const snoozed = followup.due_date !== followup.original_due_date;
  const today = todayLocalDateString();

  const handleSnooze = (days: string) => {
    const newDate = addDays(today, parseInt(days));
    patchFollowup.mutate({ followupId: followup.id, data: { due_date: newDate } });
  };

  const handleMarkDone = () => {
    patchFollowup.mutate({ followupId: followup.id, data: { status: 'done' } });
  };

  const handleDismiss = () => {
    deleteFollowup.mutate(followup.id);
  };

  const isPending = patchFollowup.isPending || deleteFollowup.isPending;

  return (
    <div
      className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-white"
      data-testid={`followup-row-${followup.id}`}
    >
      {/* Icon */}
      <div className={`mt-0.5 flex-shrink-0 ${overdue ? 'text-red-500' : 'text-blue-500'}`}>
        <Bell size={16} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`text-sm font-medium ${overdue ? 'text-red-600' : 'text-gray-900'}`}
            data-testid="followup-due-date"
          >
            {formatDate(followup.due_date)}
          </span>
          {overdue && (
            <Badge variant="destructive" className="text-xs px-1.5 py-0">
              Overdue
            </Badge>
          )}
          {snoozed && (
            <span className="text-xs text-muted-foreground">
              (originally {formatDate(followup.original_due_date)})
            </span>
          )}
          {followup.expected_amount_cents !== null && (
            <span className="text-sm font-semibold text-gray-700">
              — collect {formatCents(followup.expected_amount_cents)}
            </span>
          )}
        </div>
        {followup.note && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{followup.note}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {/* Mark done */}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
          onClick={handleMarkDone}
          disabled={isPending}
          title="Mark as done"
          data-testid="mark-done-button"
        >
          <CheckCircle2 size={14} />
        </Button>

        {/* Snooze dropdown */}
        <Select onValueChange={handleSnooze} disabled={isPending}>
          <SelectTrigger
            className="h-7 w-auto px-2 text-xs border-0 bg-transparent hover:bg-gray-100"
            aria-label="Snooze follow-up"
            data-testid="snooze-select"
          >
            <Clock size={14} className="text-amber-600" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Snooze 1 day</SelectItem>
            <SelectItem value="3">Snooze 3 days</SelectItem>
            <SelectItem value="7">Snooze 1 week</SelectItem>
          </SelectContent>
        </Select>

        {/* Dismiss */}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-gray-400 hover:text-destructive hover:bg-red-50"
          onClick={handleDismiss}
          disabled={isPending}
          title="Dismiss follow-up"
          data-testid="dismiss-button"
        >
          <X size={14} />
        </Button>
      </div>
    </div>
  );
}

export function FollowupsSection({ invoiceId }: FollowupsSectionProps) {
  const { data: followups, isLoading, error } = useInvoiceFollowups(invoiceId);

  const pending = followups?.filter((f) => f.status === 'pending') ?? [];
  const done = followups?.filter((f) => f.status === 'done') ?? [];

  if (isLoading) {
    return (
      <div className="mt-6 space-y-2" data-testid="followups-section-loading">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6">
        <p className="text-sm text-muted-foreground">Failed to load follow-ups</p>
      </div>
    );
  }

  if (!followups || followups.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 space-y-3" data-testid="followups-section">
      {pending.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-700">
            Upcoming Follow-ups ({pending.length})
          </h4>
          <div className="space-y-2">
            {pending.map((f) => (
              <FollowupRow key={f.id} followup={f} invoiceId={invoiceId} />
            ))}
          </div>
        </div>
      )}

      {done.length > 0 && (
        <details className="group">
          <summary className="text-xs text-muted-foreground cursor-pointer hover:text-gray-600 list-none flex items-center gap-1">
            <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
            {done.length} completed follow-up{done.length !== 1 ? 's' : ''}
          </summary>
          <div className="mt-2 space-y-2 opacity-60">
            {done.map((f) => (
              <div
                key={f.id}
                className="flex items-center gap-2 p-2 rounded border border-gray-100 text-sm"
              >
                <CheckCircle2 size={14} className="text-green-500 flex-shrink-0" />
                <span className="text-muted-foreground">{formatDate(f.due_date)}</span>
                {f.expected_amount_cents !== null && (
                  <span className="text-muted-foreground">
                    — {formatCents(f.expected_amount_cents)}
                  </span>
                )}
                {f.note && (
                  <span className="text-muted-foreground truncate">{f.note}</span>
                )}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
